import { useCallback, useState } from "react";
import { AuthImage } from "@/components/AuthImage";
import { API } from "@/lib/api";

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";

const SIZES = { sm: "h-8 w-8 text-[0.65rem]", md: "h-10 w-10 text-xs", lg: "h-20 w-20 text-lg", xl: "h-28 w-28 text-2xl" };

// Round avatar. Falls back to initials when there is no picture or it cannot be loaded.
// `sig` switches to the public (signed) image path so signed-out visitors see the picture too.
export const Avatar = ({ imageId, sig, name, size = "md", testid = "avatar", className = "" }) => {
  const [failed, setFailed] = useState(false);
  const onFail = useCallback(() => setFailed(true), []);
  const box = `${SIZES[size] || SIZES.md} shrink-0 overflow-hidden rounded-full ${className}`;

  if (imageId && !failed) {
    return (
      <div className={`${box} border border-border bg-slate-100`} data-testid={testid}>
        {sig ? (
          <img
            src={`${API}/images/public/${imageId}?sig=${sig}`}
            alt={name || "Profile picture"}
            data-testid={`${testid}-img`}
            onError={onFail}
            className="h-full w-full object-cover"
          />
        ) : (
          <AuthImage
            imageId={imageId}
            alt={name || "Profile picture"}
            testid={`${testid}-img`}
            onFail={onFail}
            className="h-full w-full object-cover"
          />
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      className={`${box} flex items-center justify-center bg-[#002FA7] font-semibold uppercase tracking-wider text-white`}
      aria-label={name}
    >
      {initials(name)}
    </div>
  );
};

// Wide banner used at the top of a profile and on organisation cards.
export const CoverPhoto = ({ imageId, sig, name, testid = "cover", className = "h-32 sm:h-44" }) => (
  <div data-testid={testid} className={`w-full overflow-hidden border border-border bg-gradient-to-r from-[#0F172A] via-[#002FA7] to-[#0F172A] ${className}`}>
    {imageId && (sig ? (
      <img src={`${API}/images/public/${imageId}?sig=${sig}`} alt={`${name || ""} cover`} data-testid={`${testid}-img`} className="h-full w-full object-cover" />
    ) : (
      <AuthImage imageId={imageId} alt={`${name || ""} cover`} testid={`${testid}-img`} className="h-full w-full object-cover" />
    ))}
  </div>
);
